// src/apiClient.ts
import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import { getToken } from './cookieUtil';
import { isPlatform } from '@ionic/react';
import { getCordovaToken } from './secureTokenService';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getSessionToken = async () => {
  const capacitorToken = await getCordovaToken();
  const webToken = getToken();

  if (isPlatform('cordova') && capacitorToken) {
    return capacitorToken;
  } else if (webToken) {
    return webToken;
  }
  return null;
};

// Define a custom configuration type that includes `requiresAuth`
export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  requiresAuth?: boolean;
}

// Create an Axios instance with the custom configuration type
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests if needed
});

// Request Interceptor: Attach bearer token if required
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig & CustomAxiosRequestConfig) => {
    if (config.requiresAuth) {
      const token = await getSessionToken(); // Await the token
      if (token) {
        // Ensure headers exist
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error: AxiosError) => {
    // Handle request errors
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle responses and errors globally
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response, // Simply return the response for successful requests
  (error: AxiosError) => {
    // Log the error for debugging
    console.error('API Error:', error.response || error.message);

    // Optional: Handle specific error statuses (e.g., 401 Unauthorized)
    if (error.response?.status === 401) {
      // Handle unauthorized access, e.g., redirect to login
      // window.location.href = '/login';
    }

    return Promise.reject(error); // Propagate the error to the calling function
  }
);

export default apiClient;